import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { type LibraryBook, readingTimeStats } from '@/lib/insights'
import { InsightCard, InsightEmpty } from './InsightCard'

/** Average days per book, fastest and slowest reads */
export function ReadingTimeCard({ books }: { books: LibraryBook[] }) {
  const { t } = useTranslation()
  const stats = useMemo(() => readingTimeStats(books), [books])

  return (
    <InsightCard
      icon="⏱️"
      title={t('insights.readingTime.title')}
      description={
        stats.sampleSize > 0
          ? t('insights.readingTime.basedOn', { count: stats.sampleSize })
          : undefined
      }
    >
      {stats.sampleSize === 0 ? (
        <InsightEmpty>{t('insights.readingTime.empty')}</InsightEmpty>
      ) : (
        <div className="space-y-3">
          <p className="text-2xl font-bold text-foreground">
            {t('insights.days', { count: stats.averageDays })}
            <span className="ml-1 text-sm font-normal text-muted-foreground">
              {t('insights.readingTime.perBook')}
            </span>
          </p>
          <dl className="space-y-2 text-sm">
            {stats.fastest && (
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground shrink-0">
                  {t('insights.readingTime.fastest')}
                </dt>
                <dd className="min-w-0 truncate text-right text-foreground">
                  {stats.fastest.book.title} · {t('insights.days', { count: stats.fastest.days })}
                </dd>
              </div>
            )}
            {stats.slowest && stats.sampleSize > 1 && (
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground shrink-0">
                  {t('insights.readingTime.slowest')}
                </dt>
                <dd className="min-w-0 truncate text-right text-foreground">
                  {stats.slowest.book.title} · {t('insights.days', { count: stats.slowest.days })}
                </dd>
              </div>
            )}
          </dl>
        </div>
      )}
    </InsightCard>
  )
}
